import { InfoIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { alertLimitTone, type AlertLimitTone } from '../alert-unit-limit';

const RING_SIZE_PX = 34;
const RING_CENTER = 17;
const RING_RADIUS = 13;
const RING_STROKE_WIDTH = 2.5;

const STROKE_CLASS: Record<Exclude<AlertLimitTone, 'empty'>, string> = {
    ok: 'stroke-white-100',
    near: 'stroke-warning-100',
    full: 'stroke-danger-300',
};

interface AlertsLimitProps {
    used: number;
    max: number;
}

export function AlertsLimit({ used, max }: AlertsLimitProps) {
    const { t } = useTranslation(I18nNamespace.ALERT_RULES);
    const tone = alertLimitTone(used, max);
    const circumference = 2 * Math.PI * RING_RADIUS;
    const fraction = max > 0 ? Math.min(1, used / max) : 0;
    const remaining = Math.max(0, max - used);
    const remainingLabel =
        remaining === 0
            ? t('form.sourceSelection.alertLimit.none')
            : t('form.sourceSelection.alertLimit.remaining', {
                  count: remaining,
              });

    return (
        <div
            className="flex items-center gap-2 rounded-full border border-grey-500 py-1 pl-1 pr-3"
            data-testid="alerts-limit"
        >
            <svg
                width={RING_SIZE_PX}
                height={RING_SIZE_PX}
                viewBox={`0 0 ${RING_SIZE_PX} ${RING_SIZE_PX}`}
                aria-hidden
            >
                <circle
                    cx={RING_CENTER}
                    cy={RING_CENTER}
                    r={RING_RADIUS}
                    fill="none"
                    strokeWidth={RING_STROKE_WIDTH}
                    className="stroke-grey-300"
                />
                <circle
                    cx={RING_CENTER}
                    cy={RING_CENTER}
                    r={RING_RADIUS}
                    fill="none"
                    strokeWidth={RING_STROKE_WIDTH}
                    strokeDasharray={circumference}
                    strokeLinecap={fraction > 0 ? 'round' : 'butt'}
                    transform={`rotate(-90 ${RING_CENTER} ${RING_CENTER})`}
                    style={{
                        strokeDashoffset: circumference * (1 - fraction),
                        transition: 'stroke-dashoffset 500ms ease-out',
                    }}
                    className={`transition-[stroke] duration-500 ease-out ${
                        tone === 'empty'
                            ? 'stroke-grey-300'
                            : STROKE_CLASS[tone]
                    }`}
                />
            </svg>
            <div className="flex w-40 flex-col">
                <div className="flex items-center gap-1 text-xs-medium text-grey-200">
                    <span>{t('form.sourceSelection.alertLimit.label')}</span>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                className="text-grey-200"
                                aria-label={t(
                                    'form.sourceSelection.alertLimit.label',
                                )}
                            >
                                <InfoIcon size={12} />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent variant="panel" className="max-w-80">
                            {t('form.sourceSelection.alertLimit.info', {
                                used,
                                max,
                            })}
                        </TooltipContent>
                    </Tooltip>
                </div>
                <span className="whitespace-nowrap text-sm-medium tabular-nums text-white-100">
                    {remainingLabel}
                </span>
            </div>
        </div>
    );
}
